/**
 * DateInputNZ — NZ-format date input: displays DD/MM/YYYY, stores ISO
 * YYYY-MM-DD.
 *
 * Carried across from the RMS workspace with `shared/` and DESIGN.md at
 * the fork, unchanged: a Company reading its own calls reads dates the
 * way it writes them, and a native date input renders the platform's
 * own field — in the platform's own order, which on a US-defaulted
 * browser is the wrong one.
 */
import { useEffect, useRef, useState } from "react";
import { cx } from "@shared/cx";
import forms from "./field.module.css";
import { displayToIso, isoToDisplay, maskNzDateInput } from "@shared/nz-date";

interface DateInputNZProps {
  value: string;
  onChange: (iso: string) => void;
  min?: string;
  placeholder?: string;
  hasError?: boolean;
  /**
   * The field's own look, supplied by the caller — `components/ui/field.module.css`'s
   * `.input` for an ordinary form field, or a table cell's transparent-border
   * class where this sits in a grid. The name predates CSS Modules and is
   * kept: what changed is that a class map now arrives here instead of a
   * Tailwind string.
   */
  inputStyles?: string;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  name?: string;
  ariaLabel?: string;
  onBlur?: () => void;
  onValidityChange?: (valid: boolean) => void;
}

export function DateInputNZ({
  value,
  onChange,
  min,
  placeholder = "DD/MM/YYYY",
  hasError,
  inputStyles = "",
  required,
  disabled,
  id,
  name,
  ariaLabel,
  onBlur,
  onValidityChange,
}: DateInputNZProps) {
  const [draft, setDraft] = useState<string>(() => isoToDisplay(value));
  const [parseError, setParseError] = useState(false);
  const focusedRef = useRef(false);

  useEffect(() => {
    if (focusedRef.current) return;
    setDraft(isoToDisplay(value));
    setParseError(false);
  }, [value]);

  const commit = (text: string) => {
    const iso = displayToIso(text);
    if (iso === null) {
      setParseError(true);
      onValidityChange?.(false);
      return;
    }
    setParseError(false);
    onValidityChange?.(true);
    setDraft(isoToDisplay(iso));
    if (iso !== value) onChange(iso);
  };

  const handleBlur = () => {
    focusedRef.current = false;
    commit(draft);
    onBlur?.();
  };

  const showError = hasError || parseError;

  return (
    <input
      id={id}
      name={name}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      value={draft}
      placeholder={placeholder}
      disabled={disabled}
      required={required}
      aria-label={ariaLabel}
      aria-invalid={showError || undefined}
      data-min={min || undefined}
      onFocus={() => {
        focusedRef.current = true;
      }}
      onChange={(e) => {
        setDraft(maskNzDateInput(e.target.value));
        setParseError(false);
      }}
      onBlur={handleBlur}
      // `.invalid` carries only the red border and is declared after
      // `.input` in the shared module, so composing the two states the shape
      // once and lets the invalid border win without a merge step.
      className={cx(inputStyles, showError && forms.invalid)}
    />
  );
}
